import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Icon,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useFullHeightPageFix } from 'src/utils/useFullHeightPageFix.js';
import MaestroNavbar from './blocks/MaestroNavbar.js';
import MaestroSideTray from './blocks/MaestroSideTray.js';
import TrayToggleButton from './blocks/TrayToggleButton.js';
import { videoResourcesCache } from 'src/services/VideoResourcesCache.js';

// Popular math YouTube channels
const mathChannels = [
  {
    id: 'curriculum',
    name: 'Curriculum',
    description: 'SFUSD Math Unit 5.0 lessons and materials',
    avatar: '📚',
    enabled: true,
    disabled: true // Greyed out as it's default
  },
  {
    id: 'khan-academy',
    name: 'Khan Academy',
    description: 'Free world-class education for anyone, anywhere',
    avatar: '🎓',
    enabled: true,
    disabled: false
  },
  {
    id: 'professor-leonard',
    name: 'Professor Leonard',
    description: 'Clear and comprehensive math lectures',
    avatar: '👨‍🏫',
    enabled: false,
    disabled: false
  },
  {
    id: 'patrickjmt',
    name: 'PatrickJMT',
    description: 'Making math less painful, one video at a time',
    avatar: '🔢',
    enabled: false,
    disabled: false
  },
  {
    id: 'mathologer',
    name: 'Mathologer',
    description: 'Mathematics magic and mystery',
    avatar: '🎭',
    enabled: false,
    disabled: false
  },
  {
    id: 'numberphile',
    name: 'Numberphile',
    description: 'Videos about numbers and mathematics',
    avatar: '🔮',
    enabled: false,
    disabled: false
  },
  {
    id: 'organic-chemistry-tutor',
    name: 'The Organic Chemistry Tutor',
    description: 'Math, physics, and chemistry tutorials',
    avatar: '🧪',
    enabled: false,
    disabled: false
  },
  {
    id: 'eddie-woo',
    name: 'Eddie Woo',
    description: 'Engaging math lessons from Australia',
    avatar: '🇦🇺',
    enabled: false,
    disabled: false
  }
];

const ResourcesPage = () => {
  const containerRef = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sideTrayOpen, setSideTrayOpen] = useState(!isMobile);
  const [activeTab, setActiveTab] = useState("sources");
  const [channels, setChannels] = useState(mathChannels);
  const [cachedVideos, setCachedVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cacheSize, setCacheSize] = useState(0);
  
  useFullHeightPageFix(containerRef);

  // Load cached videos when Assets tab is selected
  useEffect(() => {
    if (activeTab === 'content') {
      loadCachedVideos();
    }
  }, [activeTab]);

  const loadCachedVideos = async () => {
    try {
      setLoading(true);
      
      // Get cache size
      const size = await videoResourcesCache.getCacheSize();
      setCacheSize(size);
      
      // For now, we'll show a placeholder since getting all cached videos 
      // would require modifying the VideoResourcesCache service
      // TODO: Add getAllCachedVideos method to VideoResourcesCache
      setCachedVideos([]);
      
    } catch (error) {
      console.error('Error loading cached videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChannelToggle = (channelId) => {
    setChannels(prevChannels =>
      prevChannels.map(channel =>
        channel.id === channelId && !channel.disabled
          ? { ...channel, enabled: !channel.enabled }
          : channel
      )
    );
  };

  const clearAllCache = async () => {
    try {
      await videoResourcesCache.clearCache();
      setCachedVideos([]);
      setCacheSize(0);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  const handleNewChat = () => {
    navigate('/chat');
  };

  const toggleSideTray = () => {
    setSideTrayOpen(!sideTrayOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Helmet>
        <title>Resources - Maestro</title>
        <meta name="description" content="Manage your learning resources and video sources" />
      </Helmet>
      
      {/* Side Tray */}
      <MaestroSideTray 
        open={sideTrayOpen}
        onClose={() => setSideTrayOpen(false)}
        onNewChat={handleNewChat}
      />
      
      {/* Toggle Button (only visible when tray is closed or on mobile) */}
      {(!sideTrayOpen || isMobile) && (
        <TrayToggleButton 
          onClick={toggleSideTray}
          open={sideTrayOpen}
        />
      )}

      {/* Main Content */}
      <Box 
        ref={containerRef} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh', 
          overflow: 'auto',
          flexGrow: 1,
          width: sideTrayOpen && !isMobile ? 'calc(100% - 300px)' : '100%',
          transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}
      >
        <MaestroNavbar />
      
        <Box 
          display="flex" 
          flexDirection="column" 
          sx={{ 
            width: "100%",
            maxWidth: { xs: "100%", sm: "600px" }, 
            p: 3,
            my: 2,
            borderRadius: "20px",
            boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
            backgroundColor: theme.palette.background.paper
          }}
        >
        <Typography
          variant="h4"
          sx={{ mb: 1, mt: { md: 3 }, fontWeight: "600" }}
        >
          Resources
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your learning sources and saved content.
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 3, width: "100%", alignSelf: "center" }}
        >
          <Tab label="Sources" value="sources" disableRipple />
          <Tab label="Content" value="content" disableRipple />
        </Tabs>

        {/* Sources Tab */}
        <Box role="tabpanel" hidden={activeTab !== "sources"}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "20px",
              boxShadow: theme.shadows.cardLg
                ? theme.shadows.cardLg(theme.palette, theme.palette.mode)
                : theme.shadows[8],
              backgroundColor: theme.palette.background.paper,
              border: "none"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.secondary', mb: 2 }}>
                CONTENT SOURCES
              </Typography>
              
              <List sx={{ width: '100%' }}>
                {channels.map((channel, index) => (
                  <ListItem
                    key={channel.id}
                    sx={{
                      borderRadius: '8px',
                      mb: 1,
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: channel.disabled ? 'transparent' : 'action.hover'
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: 'transparent',
                        fontSize: '1.5rem'
                      }}
                    >
                      {channel.avatar}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: channel.disabled ? 'text.disabled' : 'text.primary'
                          }}
                        >
                          {channel.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: channel.disabled ? 'text.disabled' : 'text.secondary'
                          }}
                        >
                          {channel.description}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={channel.enabled}
                        onChange={() => handleChannelToggle(channel.id)}
                        disabled={channel.disabled}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Content Tab */}
        <Box role="tabpanel" hidden={activeTab !== "content"}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "20px",
              boxShadow: theme.shadows.cardLg
                ? theme.shadows.cardLg(theme.palette, theme.palette.mode)
                : theme.shadows[8],
              backgroundColor: theme.palette.background.paper,
              border: "none"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                  SAVED CONTENT
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={`${cacheSize} saved content`} 
                    size="small" 
                    variant="outlined"
                  />
                  {cacheSize > 0 && (
                    <Chip 
                      label="Clear Cache" 
                      size="small" 
                      color="error"
                      variant="outlined"
                      onClick={clearAllCache}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : cacheSize === 0 ? (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                  No recently viewed content found. Content is automatically saved when you view lesson resources.
                </Alert>
              ) : (
                <Alert severity="success" sx={{ borderRadius: '12px' }}>
                  {cacheSize} lesson plans have cached video resources. This helps reduce loading times and API usage.
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                All resources are automatically cached for 24 hours when you view lesson plans. 
                This improves performance and reduces API calls to YouTube.
              </Typography>
            </CardContent>
          </Card>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResourcesPage; 